// src/views/DashboardView.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { TestingButton } from '@/components/dashboard/TestingButton';
import { StreakCounter } from '@/components/dashboard/StreakCounter';
import { QuickStats } from '@/components/dashboard/QuickStats';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { TopPerformers } from '@/components/leaderboard/TopPerformers';
import { WelcomePrompt } from '@/components/dashboard/WelcomePrompt';
import { useAuth } from '@/contexts/AuthContext';
import { useTestingData } from '@/hooks/useTestingData';
import { useLeaderboard } from '@/hooks/useLeaderboard';
import { getRecentActivity } from '@/lib/firestore';
import { TestingEntry } from '@/types';

export const DashboardView: React.FC = () => {
  const { user } = useAuth();
  const { stats, hasTested, refresh } = useTestingData(user?.uid);
  const { leaderboard } = useLeaderboard();
  const [recentActivity, setRecentActivity] = useState<TestingEntry[]>([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const testingButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadRecentActivity();
  }, []);

  // Show welcome prompt if user hasn't tested today (based on DB, not localStorage)
  useEffect(() => {
    if (stats && !hasTested && user) {
      console.log('Welcome prompt check:', { 
        hasStats: !!stats, 
        hasTested, 
        userId: user.uid,
        shouldShow: !hasTested 
      });
      
      // Show prompt after delay for dramatic effect
      // Only show if user hasn't tested (checked against database)
      const timer = setTimeout(() => {
        console.log('🎯 Showing welcome prompt!');
        setShowWelcome(true);
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      console.log('Welcome prompt NOT showing:', { 
        hasStats: !!stats, 
        hasTested, 
        hasUser: !!user 
      });
    }
  }, [stats, hasTested, user]);

  const loadRecentActivity = async () => {
    try {
      const activity = await getRecentActivity(10);
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  const handleTestingSuccess = async () => {
    console.log('🔄 handleTestingSuccess called - refreshing dashboard...');
    
    // Wait a moment for Firestore to update
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Refresh stats and activity
    await refresh();
    await loadRecentActivity();
    
    console.log('✅ Dashboard refresh complete');
  };

  const handleTestFromModal = async () => {
    if (!user) return;
    
    try {
      console.log('📝 Recording test from modal...');
      const { recordTestingEntry } = await import('@/lib/firestore');
      await recordTestingEntry(user.uid, user.displayName, user.photoURL);
      
      console.log('✅ Test recorded successfully!');
      
      // Wait a moment for Firestore to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh stats and activity
      await refresh();
      await loadRecentActivity();
      
      console.log('🔄 Dashboard refreshed');
    } catch (error) {
      console.error('Error recording testing from modal:', error);
      throw error; // Re-throw so modal can handle it
    }
  };

  // Show dashboard even while loading (optimistic UI)
  const displayStats = stats || {
    currentStreak: 0,
    longestStreak: 0,
    totalDays: 0,
    weeklyTests: 0,
    monthlyTests: 0,
    userId: user?.uid || '',
    userName: user?.displayName || '',
    userPhoto: user?.photoURL || '',
    lastTestDate: '',
    achievements: [],
  };

  const userRank = displayStats && user
    ? leaderboard.findIndex((u) => u.userId === user?.uid) + 1
    : 0;

  return (
    <>
      {/* Welcome Prompt Modal */}
      {showWelcome && displayStats && (
        <WelcomePrompt
          userName={user?.displayName?.split(' ')[0] || 'there'}
          currentStreak={displayStats.currentStreak}
          onDismiss={() => setShowWelcome(false)}
          onTestNow={handleTestFromModal}
          onSuccess={handleTestingSuccess}
        />
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        <div>
          <h1 className="text-4xl font-bold mb-2">
            Welcome back, {user?.displayName?.split(' ')[0]}! 👋
          </h1>
          <p className="text-muted-foreground">
            Track your daily mobile app testing and watch your streak grow
          </p>
        </div>

        <div ref={testingButtonRef}>
          <TestingButton onSuccess={handleTestingSuccess} hasTested={hasTested} />
        </div>

        <StreakCounter
          currentStreak={displayStats.currentStreak}
          longestStreak={displayStats.longestStreak}
          totalDays={displayStats.totalDays}
        />

        <QuickStats
          weeklyTests={displayStats.weeklyTests}
          monthlyTests={displayStats.monthlyTests}
          teamSize={leaderboard.length}
          rank={userRank || leaderboard.length + 1}
        />

        <div className="grid md:grid-cols-2 gap-6">
          <TopPerformers topThree={leaderboard.slice(0, 3)} />
          <RecentActivity activities={recentActivity} />
        </div>
      </motion.div>
    </>
  );
};

